import React, { useState, useEffect } from 'react';
import ThemedButton from '../Shared/ThemedButton';
import PoliciesAgreementNote from './PoliciesAgreementNote';
import { getBookingPageTheme } from '../../utility/themeUtils';
import { formatPriceWithCurrency } from '../../utility/priceUtils';
import { requiresApproval, calculateBookingTotals, getContinueButtonText } from '../../utility/bookingFlowUtils';
import { useBookingFlow } from '../../contexts/BookingFlowContext';

const FloatingBookingButton = () => {
  const {
    bookingPage,
    selectedServices,
    onContinue,
    bookingWorkflowRef,
    continueDisabled,
    travelFee,
    selectedAppointmentType,
    currency,
    outsideAvailabilityRequested,
    customTimeRequested,
    currentBookingStep,
    currentBookingError,
    validationErrors,
    contactFormValidationFailed,
    isSubmittingBooking,
  } = useBookingFlow();

  const { primaryColor, themeMode } = getBookingPageTheme(bookingPage);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  const approvalRequired = requiresApproval(outsideAvailabilityRequested, customTimeRequested, bookingPage?.autoApproval);
  const { totalPrice, totalItems } = calculateBookingTotals(
    selectedServices,
    selectedAppointmentType,
    bookingPage?.travelFeesEnabled,
    travelFee
  );

  // Show/hide based on selected services
  useEffect(() => {
    const next = selectedServices.length > 0;
    setShouldShow((prev) => (prev === next ? prev : next));
  }, [selectedServices]);

  // Intersection Observer to hide when original button is visible
  useEffect(() => {
    if (!bookingWorkflowRef?.current || !shouldShow) {
      setIsVisible(shouldShow);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Hide floating button when original button is visible
        setIsVisible(!entry.isIntersecting && shouldShow);
      },
      {
        threshold: 0.1, // Trigger when 10% of target is visible
        rootMargin: '0px 0px -50px 0px' // Hide floating button slightly before original is fully visible
      }
    );

    observer.observe(bookingWorkflowRef.current);

    return () => {
      observer.disconnect();
    };
  }, [bookingWorkflowRef, shouldShow]);

  if (!isVisible) return null;

  return (
    <div className="block lg:hidden">
      {/* Floating Button Container */}
      <div
        className={`fixed bottom-4 left-4 right-4 z-50 transform transition-all duration-300 ease-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="bg-themed-card border-themed rounded-lg shadow-2xl border">
          <div className="p-4">
            {/* Summary Info */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-signal-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-themed-primary">
                  {totalItems} {totalItems === 1 ? 'service' : 'services'} selected
                </span>
              </div>
              <span className="font-bold text-themed-primary">
                {formatPriceWithCurrency(totalPrice, currency, true)}
              </span>
            </div>

            {/* Error Message */}
            {(currentBookingError || Object.keys(validationErrors || {}).length > 0 || contactFormValidationFailed) && (
              <div className="mb-2 px-3 py-2 bg-signal-red-50 border border-signal-red-100 rounded-lg">
                <p className="text-xs text-signal-red-700 text-center">Please fix errors above</p>
              </div>
            )}

            {/* Continue Button */}
            <ThemedButton
              onClick={onContinue}
              primaryColor={primaryColor}
              themeMode={themeMode}
              size="large"
              className="w-full"
              disabled={continueDisabled}
            >
              {getContinueButtonText(currentBookingStep, approvalRequired, isSubmittingBooking)}
            </ThemedButton>
            {currentBookingStep === 'contact-confirmation' && (
              <PoliciesAgreementNote bookingPage={bookingPage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingBookingButton;
